/**
 * Het logboek (SPEC §7.5): elke gebeurtenis, met filter op soort.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Logbladzijde, type Logregel } from '../../api';
import { Knop, Laden, Melding } from '../../componenten/basis';

export function Logboek() {
  const [blad, zetBlad] = useState<Logbladzijde | null>(null);
  const [soort, zetSoort] = useState<string>('');
  const [pagina, zetPagina] = useState(1);
  const [fout, zetFout] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams();
    if (soort) params.set('soort', soort);
    params.set('pagina', String(pagina));
    api.haal<Logbladzijde>(`/api/beheer/logboek?${params}`).then(zetBlad).catch((f: Error) => zetFout(f.message));
  }, [soort, pagina]);

  if (fout) return <Melding soort="fout">{fout}</Melding>;
  if (!blad) return <Laden />;

  const paginas = Math.max(1, Math.ceil(blad.totaal / 50));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <select
          className="veld w-auto"
          value={soort}
          onChange={(e) => {
            zetSoort(e.target.value);
            zetPagina(1);
          }}
        >
          <option value="">Alle soorten</option>
          {blad.soorten.map((s) => (
            <option key={s.soort} value={s.soort}>
              {s.soort} ({s.aantal})
            </option>
          ))}
        </select>
        <span className="text-sm text-inkt-zacht">{blad.totaal} regels</span>
        {paginas > 1 && (
          <span className="ml-auto flex items-center gap-2 text-sm">
            <Knop soort="stil" klein disabled={pagina <= 1} onClick={() => zetPagina((p) => p - 1)}>
              ←
            </Knop>
            {pagina} / {paginas}
            <Knop soort="stil" klein disabled={pagina >= paginas} onClick={() => zetPagina((p) => p + 1)}>
              →
            </Knop>
          </span>
        )}
      </div>
      <div className="kaart overflow-x-auto">
        <Logboektabel regels={blad.regels} />
      </div>
    </div>
  );
}

export function Logboektabel({ regels, zonderHeld = false }: { regels: Logregel[]; zonderHeld?: boolean }) {
  if (regels.length === 0) return <p className="py-6 text-center text-sm text-inkt-zacht">Nog niets gebeurd.</p>;
  return (
    <table className="tabel">
      <thead>
        <tr>
          <th>Wanneer</th>
          <th>Wat</th>
          {!zonderHeld && <th>Held</th>}
          <th>Door</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        {regels.map((r) => (
          <tr key={r.id}>
            <td className="whitespace-nowrap text-inkt-zacht">{nieuwsteEerst(r.op)}</td>
            <td className="font-medium">{r.soort.replaceAll('_', ' ')}</td>
            {!zonderHeld && <td>{r.held_id ? <Link to={`/beheer/helden/${r.held_id}`}>{r.held_naam ?? 'held'}</Link> : <span className="text-inkt-stil">-</span>}</td>}
            <td className="text-inkt-zacht">{r.door}</td>
            <td className="max-w-md break-words font-mono text-xs text-inkt-zacht">{alsTekst(r.details)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function nieuwsteEerst(op: string): string {
  const d = new Date(op);
  if (Number.isNaN(d.getTime())) return op;
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function alsTekst(details: unknown): string {
  if (!details || typeof details !== 'object') return '';
  return Object.entries(details as Record<string, unknown>)
    .filter(([, w]) => w !== null && w !== undefined && w !== '')
    .map(([k, w]) => `${k}: ${typeof w === 'object' ? JSON.stringify(w) : String(w)}`)
    .join(' · ');
}
